import {Injectable} from '@angular/core';
import {ApiCallService} from "./api-call.service";
import {Idle} from "@ng-idle/core";
import {AppConstants} from "../app-constants";
import {CookieService} from "ngx-cookie-service";
import {first} from "rxjs/operators";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { LocalStorageService } from "./local-storage.service";
import { SingleDashboardWidget } from "./dashboard-widget.service";
import {environment} from "../../environments/environment";
import {AccountStatus} from "../interfaces/account-status";
import {PermissionService} from "./permissions.service";

@Injectable({
  providedIn: 'root'
})
export class Session {

  private userData: any;
  private userId: number;
  private userRequestKey: string;
  private username: string;
  private userFullName: string;
  private userRoles: any;
  private appPermissions: any;
  private accreditorList: any[];
  private accreditorToolsPermissions: any;
  private standardsPermissions: any;
  private userManagementPermissions: any;
  private weaveToolPermissions: any;
  private responseRoles: object;
  private userOrganizationData: any;
  private orgLogo: string;
  private userAvatar: string;
  private workspaces: Array<object>;
  private valid: boolean;
  private organizationInfoLoaded: boolean;
  private evaluatorPrograms: any;
  private termsAndConditionsAcceptedDate: Date;
  private privacyStatementAcceptedDate: Date;
  private evaluatorRoleProgramRequestKey: string;
  private editDirectoryRequestKey: string;
  private editProgramDirectoryRequestKey: string;
  private editDirectoryInstitutions: accreditationProfile[];
  private editProgramDirectoryInstitutions: programProfile[];
  private currentUserWidgetsList: any[] = []; //the type is what the server sends
  private isAccreditorDirectoryInstitution: boolean;
  private isAccreditorDirectoryLiaison: boolean;
  private lastLoginDate: Date;
  private accountStatus: AccountStatus;

  private _isProgramLeader: boolean;
  private editProgramDirectoryShowAdvisoryTab: boolean;
  private editProgramDirectoryShowFacultyTab: boolean;
  private editProgramDirectoryEditAdvisoryTab: boolean;
  private editProgramDirectoryEditFacultyTab: boolean;
  private editInstitutionalDirectoryShowAdvisoryTab: boolean;
  private editInstitutionalDirectoryShowFacultyTab: boolean;
  private editInstitutionalDirectoryEditAdvisoryTab: boolean;
  private editInstitutionalDirectoryEditFacultyTab: boolean;
  public getPermissionToShow: menuPermissions;
  private lightModeSource = new BehaviorSubject<string>("lightMode"); //default is light!
  public lightMode = this.lightModeSource.asObservable();
  private localStorageKey:string = "lightMode";

  constructor(
    private ApiService: ApiCallService,
    private idle: Idle,
    private LocalStorage: LocalStorageService,
    private cookieService: CookieService,
    private permissionService: PermissionService,
  ) {
    this.organizationInfoLoaded = false;
    if(JSON.parse(localStorage.getItem('organizationInfo'))){
      this.organizationInfoLoaded = true;
      this.setPermissionToShow();
    }
    /*let light = this.getLightModeFromStorage();
    if(light) {
      this.lightModeSource.next(light);
    }*/
  }

  create(userData: any) {
    this.userData = userData;
    this.userId = userData.id;
    this.userRequestKey = userData.requestKey;
    this.userFullName = userData.name;
    this.userRoles = userData.roles;
    this.appPermissions = userData.appPermissions;
    this.accreditorList = userData.accreditorList;
    this.accreditorToolsPermissions = userData.accreditorToolsPermissions;
    this.standardsPermissions = userData.standardsPermissions;
    this.userManagementPermissions = userData.userManagementPermissions;
    this.weaveToolPermissions = userData.weaveToolPermissions;
    this.userOrganizationData = userData.organization;
    this.evaluatorPrograms = userData.evaluatorPrograms;
    this._isProgramLeader = userData.isProgramLeader;
    this.editProgramDirectoryShowAdvisoryTab = userData.editProgramDirectoryShowAdvisoryTab;
    this.editProgramDirectoryShowFacultyTab = userData.editProgramDirectoryShowFacultyTab;
    this.editProgramDirectoryEditAdvisoryTab = userData.editProgramDirectoryEditAdvisoryTab;
    this.editProgramDirectoryEditFacultyTab = userData.editProgramDirectoryEditFacultyTab;
    this.editInstitutionalDirectoryShowAdvisoryTab = userData.editInstitutionalDirectoryShowAdvisoryTab;
    this.editInstitutionalDirectoryShowFacultyTab = userData.editInstitutionalDirectoryShowFacultyTab;
    this.editInstitutionalDirectoryEditAdvisoryTab = userData.editInstitutionalDirectoryEditAdvisoryTab;
    this.editInstitutionalDirectoryEditFacultyTab = userData.editInstitutionalDirectoryEditFacultyTab;
    this.termsAndConditionsAcceptedDate = userData.termsAndConditionsAcceptedDate === "0001-01-01T00:00:00" ? null : new Date(userData.termsAndConditionsAcceptedDate);
    this.privacyStatementAcceptedDate = userData.privacyStatementAcceptedDate === "0001-01-01T00:00:00" ? null : new Date(userData.privacyStatementAcceptedDate);
    this.lastLoginDate = userData.lastLogin === "0001-01-01T00:00:00" ? null : new Date(userData.lastLogin);
    if (userData.organization) {
      this.orgLogo = userData.organization.logo;
    }
    this.username = userData.username;
    this.userAvatar = userData.profilePicture;
    this.workspaces = AppConstants.workspaces;
    this.evaluatorRoleProgramRequestKey = userData.evaluatorRoleProgramRequestKey;
    this.editDirectoryRequestKey = userData.editDirectoryRequestKey;
    this.editProgramDirectoryRequestKey = userData.editProgramDirectoryRequestKey;
    this.editDirectoryInstitutions = userData.editDirectoryInstitutions;
    this.editProgramDirectoryInstitutions = userData.editProgramDirectoryInstitutions;
    this.isAccreditorDirectoryInstitution = userData.isAccreditorDirectoryInstitution;
    this.isAccreditorDirectoryLiaison = userData.isAccreditorDirectoryLiaison;
    this.currentUserWidgetsList = userData.userWidgets;
    this.accountStatus = this.createAccountStatus(userData.accountStatus);
    localStorage.setItem('widgets', JSON.stringify(this.currentUserWidgetsList));
    this.responseRoles = {};
    localStorage.setItem('user', this.getJSONString());
    localStorage.setItem('organizationInfo', JSON.stringify(this.userData.organization));
    this.renewSessionExpireTime();
    this.organizationInfoLoaded = true;
    this.setPermissionToShow(); // This will be deprecated soon
    this.permissionService.setPermissions('accountPermissions', userData.accountPermissions);
  }

  getJSONString() {
    return JSON.stringify({
      userData: this.userData,
      userId: this.userId,
      userRequestKey: this.userRequestKey,
      username: this.username,
      userFullName: this.userFullName,
      userRoles: this.userRoles,
      responseRoles: this.responseRoles,
      userOrganizationData: this.userOrganizationData,
      termsAndConditionsAcceptedDate: this.termsAndConditionsAcceptedDate,
      privacyStatementAcceptedDate: this.privacyStatementAcceptedDate,
      orgLogo: this.orgLogo,
      userAvatar: this.userAvatar,
      workspaces: this.workspaces,
      appPermissions: this.appPermissions,
      accreditorList: this.accreditorList,
      valid: this.valid,
      accreditorToolsPermissions: this.accreditorToolsPermissions,
      standardsPermissions: this.standardsPermissions,
      userManagementPermissions: this.userManagementPermissions,
      weaveToolPermissions: this.weaveToolPermissions,
      evaluatorPrograms: this.evaluatorPrograms,
      evaluatorRoleProgramRequestKey: this.evaluatorRoleProgramRequestKey,
      editDirectoryRequestKey: this.editDirectoryRequestKey,
      editProgramDirectoryRequestKey: this.editProgramDirectoryRequestKey,
      editDirectoryInstitutions: this.editDirectoryInstitutions,
      editProgramDirectoryInstitutions: this.editProgramDirectoryInstitutions,
      accountStatus: this.accountStatus,
      _isProgramLeader: this._isProgramLeader,
      editProgramDirectoryShowAdvisoryTab: this.editProgramDirectoryShowAdvisoryTab,
      editProgramDirectoryShowFacultyTab: this.editProgramDirectoryShowFacultyTab,
      editProgramDirectoryEditAdvisoryTab: this.editProgramDirectoryEditAdvisoryTab,
      editProgramDirectoryEditFacultyTab: this.editProgramDirectoryEditFacultyTab,
      editInstitutionalDirectoryShowAdvisoryTab: this.editInstitutionalDirectoryShowAdvisoryTab,
      editInstitutionalDirectoryShowFacultyTab: this.editInstitutionalDirectoryShowFacultyTab,
      editInstitutionalDirectoryEditAdvisoryTab: this.editInstitutionalDirectoryEditAdvisoryTab,
      editInstitutionalDirectoryEditFacultyTab: this.editInstitutionalDirectoryEditFacultyTab,
    })
  }

  private createAccountStatus(accountStatus: any): AccountStatus {
    return {
      status: accountStatus.accountStatus === 0 ? 'ok' : accountStatus === 1 ? 'noAccount' : 'error',
      name: accountStatus.accountName,
      subscription: {
        name: accountStatus.subscriptionName,
        price: accountStatus.subscriptionPrice,
        usersPrice: accountStatus.subscriptionUserPrice,
        nextBillingDate: accountStatus.subscriptionNextChargeDate,
        isTrial: accountStatus.subscriptionrequestKey === "{E4E5D5CC-D27A-4961-BF1C-96A71BEDA652}",
        requestKey: accountStatus.subscriptionrequestKey,
      },
      users: accountStatus.accountUsers.map((user: any) => {
        return {
          name: user.name,
          email: user.email,
          pending: user.pending,
        }
      })
    }
  }

  public getAccountStatus(): AccountStatus{
    if(this.accountStatus) return this.accountStatus;
    const user = this.getUser();
    if(user){
      return user.accountStatus;
    }
    return null;
  }

  public setAccountStatusUsers(users: {
    name: string,
    email: string,
    pending: boolean,
  }[]){
    const user = this.getUser();
    if(user){
      const accountStatus = user.accountStatus;
      accountStatus.users = users;
      //save to the localstorage again
      localStorage.setItem('user', JSON.stringify(user));
    }
  }

  setLogoutUrl(url: string){
    localStorage.setItem("logoutUrl", url);
  }

  getLogoutUrl(){
    return localStorage.getItem("logoutUrl");
  }

  destroy() {
    this.userData = null;
    this.userRoles = null;
    this.userOrganizationData = null;
    this.userId = null;
    this.userRequestKey = null;
    this.userFullName = null;
    this.termsAndConditionsAcceptedDate = null;
    this.privacyStatementAcceptedDate = null;
    this.username = null;
    this.orgLogo = null;
    this.userAvatar = null;
    this.responseRoles = null;
    this.appPermissions = null;
    this.accreditorList = null;
    this.accreditorToolsPermissions = null;
    this.standardsPermissions = null;
    this.userManagementPermissions = null;
    this.weaveToolPermissions = null;
    this.currentUserWidgetsList = [];
    localStorage.removeItem('user');
    localStorage.removeItem('organizationInfo');
    localStorage.removeItem('originalUser');
    localStorage.removeItem('logoutUrl');
    localStorage.removeItem('widgets');
    this.permissionService.clearPermissions();
    this.deleteSessionExpireTime();
  }

  impersonateUser(userData: any) {
    const originalUser = JSON.parse(localStorage.user);
    this.destroy();
    this.create(userData);
    localStorage.setItem('originalUser', JSON.stringify(originalUser));
  }

  stopImpersonateUser() {
    const originalUser = JSON.parse(localStorage.originalUser);
    this.destroy();
    return originalUser;
  }

  isImpersonatedUser() {
    return typeof localStorage.originalUser !== 'undefined';
  }

  reload(): Observable<boolean>{
    let subject = new Subject<boolean>();
    let originalUser = "";
    if(this.isImpersonatedUser()){
      originalUser = localStorage.originalUser;
    }
    this.destroy();
    this.ApiService.getData("/getusersession/", {}).pipe(first()).subscribe((response) => {
      if (response.success) {
        const userData = response.data[0].dataSet;
        this.create(userData);
        if(originalUser){
          localStorage.setItem('originalUser', originalUser);
        }
        subject.next(true);
      }
      else {
        subject.next(false);
      }
    })
    return subject.asObservable();
  }

  renewSessionExpireTime(){
    const user = JSON.parse(localStorage.getItem('user'));
    if(!user){
      return;
    }
    let userId = user.userId;
    let expiredSessionTime = new Date();

    const oneHour = 60 * 60 * 1000;
    expiredSessionTime.setTime(expiredSessionTime.getTime() + oneHour);
    this.cookieService.set('weaveVisit', userId, null, '/', '', true, 'Lax');
    this.cookieService.set('expiredSessionTime', expiredSessionTime.toString(), expiredSessionTime, '/', '', true);
  }

  isSessionExpired(){
    const now = new Date();
    const expiredSessionTime = this.cookieService.get('expiredSessionTime');
    const weaveVisit = this.cookieService.get('weaveVisit');

    if(!weaveVisit){
      return true;
    }

    if(!expiredSessionTime){
      return true;
    }
    return new Date(expiredSessionTime) < now;
  }

  deleteSessionExpireTime(){
    this.cookieService.delete('expiredSessionTime');
    this.cookieService.delete('weaveVisit');
  }

  getUser() {
    if(this.isSessionExpired()){
      return null;
    }
    return JSON.parse(localStorage.getItem('user'))  || null;
  };

  getResponseRole(responseGUID: string) {
    let Session = this;
    let user = Session.getUser();
    let key = responseGUID.toString();

    if (user.responseRoles[key]) {
      // reading team member object from localStorage cache...
      // returns a promise, since 'resolve' expects one...
      return new Promise((resolve, reject) => {
        resolve(user.responseRoles[key]);
      }).then(function (cachedTeamMemberObject) {
        return cachedTeamMemberObject;
      });
    } else {
      // fetching team member object from server...
      return this.ApiService.getDataAsPromise('/aeAMSgetResponseTeamMember/', {
        responseRequestKey: key,
        userRequestKey: user.userRequestKey
      }).then((response) => {
        if (response.dataError) response.dataError.forEach(function (dataError) {
          if (response.success) console.log(dataError.description);
          else console.error(dataError.description);
        });

        if (response.success && response.data[0]) {
          user.responseRoles[key] = response.data[0].dataSet;
          localStorage.setItem('user', JSON.stringify(user));
          return user.responseRoles[key];
        } else {
          return null;
        }
      });
    }
  };

  logoutUser() {
    this.destroy();
    this.idle.stop();
  };

  getOrganizationInfo() {
    let organizationInfo = JSON.parse(localStorage.getItem('organizationInfo'));
    if (organizationInfo) {
      return new Promise((resolve, reject) => {
        resolve(organizationInfo);
      }).then(function (cachedOrganizationInfoObject) {
        return cachedOrganizationInfoObject;
      });
    } else {
      return this.ApiService.getDataAsPromise('/aeAMSgetOrganizationInfo/', {organizationRequestKey: this.getUser().userOrganizationData.requestKey}).
      then((response) => {
        if (response.success && response.data[0]) {
          organizationInfo = response.data[0].dataSet;
          this.organizationInfoLoaded = true;
          localStorage.setItem('organizationInfo', JSON.stringify(organizationInfo));
          this.setPermissionToShow();
          return organizationInfo;
        } else {
          return null;
        }
      });
    }
  };

  getOrganizationInfoFromCache(){
    return JSON.parse(localStorage.getItem('organizationInfo'));
  }

  //This is a function that checks if the currently logged in user belongs to a strictly accreditor plan.
  //That means that the user has no access to Credentials and Assessment
  //This will be true for all 3.0 Institutions
  isAccreditorPlan() {
    const currUser = this.getUser();
    return this.organizationInfoLoaded &&
      //!this.isFreemiumPlan() &&
      !currUser.userData.organization.allowAssessment &&
      !currUser.userData.organization.allowCredentials;
  }

  isFreemiumPlan(){
    const currUser = this.getUser();
    return this.organizationInfoLoaded &&
      currUser.userData.organization.isFreemium;
  }

  isShibbolethAuthentication(){
    const currentUser = this.getUser();
    if(currentUser) return currentUser.userData.organization.authenticationMethodGuid && currentUser.userData.organization.authenticationMethodGuid === AppConstants.authenticationMethod.shibboleth;
  }

  setOrganizationInfoLoaded(bool: boolean) {
    this.organizationInfoLoaded = bool;
  }

  getUserOrganization() {
    const currUser = this.getUser();
    if(currUser){
      return currUser.userData.organization;
    }
    return null;
  };

  isVerifyFacilitator() {
    const currentUser = this.getUser();
    if(currentUser){
      return currentUser.userRoles.sysAdmin || currentUser.userRoles.pcAdmin || currentUser.userRoles.isOrgAdmin || currentUser.userRoles.isVerifyManager || currentUser.userRoles.isVerifyFacilitator;
    }
    return false;
  }

  isVerifyManager() {
    const currentUser = this.getUser();
    if(currentUser){
      return currentUser.userRoles.sysAdmin || currentUser.userRoles.pcAdmin || currentUser.userRoles.isOrgAdmin || currentUser.userRoles.isVerifyManager;
    }
    return false;
  }

  getDashboardType(): DashboardType{
    const currentUser = this.getUser();

    return currentUser.appPermissions.dashboardId;
  }

  getProgramAccreditor(){
    //This is just for now, until we will find out how we will cover multiple accreditors, we will just return the first entry
    const currUser = this.getUser();
    if(currUser.userData.accreditorList && currUser.userData.accreditorList.length) return currUser.userData.accreditorList[0];
    return null;
  }

  getAnnualUpdateLabelOfDirectoryProgram(programDirectoryRequestKey: string):string {
    const currUser = this.getUser();
    const programDirectory = currUser.editProgramDirectoryInstitutions.find( inst => inst.institutionRequestKey === programDirectoryRequestKey );
    if( programDirectory && programDirectory.annualUpdateCaption ) {
      return programDirectory.annualUpdateCaption;
    } else {
      return "Annual Update";
    }
  }

  getAccreditorAnnualUpdateLabel(isProgrammatic: boolean = true):string {
    const currUser = this.getUser();
    if(isProgrammatic && currUser.userData.editProgramDirectoryAnnualUpdateCaption) {
      //programmatic
      return currUser.userData.editProgramDirectoryAnnualUpdateCaption;
    } else if(currUser.userData.editDirectoryAnnualUpdateCaption) {
      //institutional
      return currUser.userData.editDirectoryAnnualUpdateCaption;
    } else {
      //default
      return "Annual Update";
    }
  }

  getInstitutionDirectoryProfiles(): accreditationProfile[]{
    return this.getUser().editDirectoryInstitutions;
  }

  getProgramInstitutionDirectoryProfiles(): programProfile[]{
    return this.getUser().editProgramDirectoryInstitutions;
  }

  getAccountAccreditationProgramRequestKey():string {
    if(this.getUser().userData.currentUserAccount?.hasEvent === true){
      return this.getUser().userData.currentUserAccount?.accredProjectRequestKey;
    }
    return null;
  }
  getDirectoryProgramRequestKey():string {
    return this.getUser().userData.currentUserAccount?.directoryProgramRequestKey;
  }
  getAccountDirectoryInstitutionRequestKey():string {
    return this.getUser().userData.currentUserAccount?.directoryInstitutionRequestKey;
  }

  getOtherAccounts(): accountInfo[] {
    if(this.isReviewer()) { //if reviewer, show all accounts
      return this.getUser().userData.userAccounts;
    } else { //otherwise exclude the current account
      return this.getUser().userData.userAccounts.filter( ( account: any ) =>
        account.accountRequestKey !== this.getUser().userData.currentUserAccount?.accountRequestKey &&
        account.accountName != "Independent Reviewers" //do not show independent reviewers accounts
      );
    }
  }

  isReviewer() {
    return !!this.getUser().evaluatorRoleProgramRequestKey;
  }

  getCurrentAccount(): accountInfo {
    return this.getUser().userData.currentUserAccount;
  }

  isAdmin():boolean {
    return this.getUser().userData.currentUserAccount?.userRole === 2;
  }

  getEvaluatorRoles(): evaluatorRole[]{
    const currentUser = this.getUser();
    if(!currentUser){
      return [];
    }
    let evaluatorRoles: evaluatorRole[] = [];
    for(let program of currentUser.evaluatorPrograms){
      evaluatorRoles.push({
        institutionName: program.institutionName,
        programName: program.programName,
        program: {
          name: program.programName,
          requestKey: program.requestKey,
          institutionDirectoryRequestKey: program.institutionDirectoryRequestKey,
          programDirectoryProgramRequestKey: program.programDirectoryProgramRequestKey,
          programDirectoryRequestKey: program.programDirectoryRequestKey,
        },
        accreditor: {
          name: program.accreditorName,
          abbreviation: program.accreditorAbbreviation,
          thumbnail: program.accreditorThumbnail,
          logo: program.accreditorImage,
        },
        role: "Reviewer"
      });
    }
    return evaluatorRoles;
  }

  setPermissionToShow() {
    const debug = environment.enableDebug;
    this.getPermissionToShow = {
      dashboard:
        this.getUser() &&
        this.organizationInfoLoaded && //Make dashboard available to everyone except reviewers & accreditors
        !this.isEvaluatorWorkingForOtherInstitution(),
      projects:
        this.getUser() &&
        this.getUser().appPermissions.showProjectsTab,
      credentials:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getOrganizationInfoFromCache().allowCredentials,
      institutions:
        this.getUser() &&
        //this.organizationInfoLoaded &&
        this.getUserOrganization().isAccreditor,
      institutionsDirectory:
        this.getUser() &&
        this.getUserOrganization().isAccreditor &&
        this.getUser().editDirectoryRequestKey !== '' && this.getUser().editDirectoryRequestKey !== undefined && this.getUser().editDirectoryRequestKey !== null,
      programsDirectory:
        this.getUser() &&
        this.getUserOrganization().isAccreditor &&
        this.getUser().editProgramDirectoryRequestKey !== '' && this.getUser().editProgramDirectoryRequestKey !== undefined && this.getUser().editProgramDirectoryRequestKey !== null,
      federationDirectory:
        this.getUser() &&
        this.getUserOrganization().isFederation,
      isAccreditorDirectoryInstitution:
        this.getUser() &&
        this.getUser().isAccreditorDirectoryInstitution,
      isAccreditorDirectoryLiaison:
        this.getUser() &&
        this.getUser().isAccreditorDirectoryLiaison,
      showApplicationTab:
        this.getUser() &&
        this.getUser().appPermissions.showApplicationTab,
      standards:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().standardsPermissions &&
        this.getUser().standardsPermissions.fullEdit,
      evaluators:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().accreditorToolsPermissions &&
        this.getUser().accreditorToolsPermissions.assignEvaluators,
      canReturnFeedback:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().accreditorToolsPermissions &&
        this.getUser().accreditorToolsPermissions.returnFeedback,
      reports:
        this.getUser() &&
        this.organizationInfoLoaded &&
        !this.isAccreditorPlan(),
      canImpersonate:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().userManagementPermissions &&
        this.getUser().userManagementPermissions.impersonateUser,
      canSearch:
        this.getUser() &&
        this.organizationInfoLoaded &&
        false,
      canCreateNewProject:
        this.getUser() &&
        !this.isAccreditorPlan(),
      attachments:
        this.getUser() &&
        this.organizationInfoLoaded &&
        !this.isAccreditorPlan(),
      organizationSettings:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().appPermissions.institutionSettings,
      courses:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().appPermissions.allowCourseManagement,
      importTool:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().appPermissions.allowImportTool,
      templates:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().appPermissions &&
        this.getUser().appPermissions.createEditAssessmentTemplate,
      ilo:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().appPermissions.institutionLevelOutcomes,
      editUserProfile:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().userManagementPermissions &&
        this.getUser().userManagementPermissions.editUserProfile,
      editUsers:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().userManagementPermissions &&
        this.getUser().userManagementPermissions.createEditUsers,
      editUsersTab:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().userManagementPermissions &&
        this.getUser().userManagementPermissions.createEditUsersTab,
      createEditUserGroup:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().userManagementPermissions &&
        this.getUser().userManagementPermissions.createEditUserGroup,
      reportSettings:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().appPermissions.reportSettings,
      inviteUser:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().weaveToolPermissions &&
        this.getUser().weaveToolPermissions.inviteUser,
      createNewProgram:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().weaveToolPermissions &&
        this.getUser().weaveToolPermissions.createNewProgram,
      createProgramEvent:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().weaveToolPermissions &&
        this.getUser().weaveToolPermissions.createProgramEvent,
      manageStandards:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().appPermissions &&
        this.getUser().weaveToolPermissions.manageStandards,
      manageProgramTemplates:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().weaveToolPermissions &&
        this.getUser().weaveToolPermissions.manageProgramTemplates,
      manageStandardsAsAccreditor:  //this is for Stacey Rowand from CAHME
        this.getUser() &&
        ( this.getUser().userRequestKey === '{af17f8bd-c2ac-4870-a9bc-47a7c71176bc}' || this.getUser().accreditorToolsPermissions.allowSelfManageTools),
      manageProgramTemplatesAsAccreditor:
        this.getUser() && //this is for Stacey Rowand from CAHME
        ( this.getUser().userRequestKey === '{af17f8bd-c2ac-4870-a9bc-47a7c71176bc}' || this.getUser().accreditorToolsPermissions.allowSelfManageTools),
      institutionLogo:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().appPermissions &&
        this.getUser().appPermissions.allowAddLogo,
      errorLog:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().appPermissions &&
        this.getUser().appPermissions.accessErrorLog,
      uiKit:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().appPermissions &&
        this.getUser().appPermissions.accessUIKit,
      eventLog:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().weaveToolPermissions &&
        this.getUser().weaveToolPermissions.viewEventLog,
      admin:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().appPermissions.accessBackend,
      annualReportsTemplateEdit:
        this.getUser() &&
        this.organizationInfoLoaded &&
        ( this.getUser().editDirectoryRequestKey || this.getUser().editProgramDirectoryRequestKey ) && this.isImpersonatedUser(),
      projectAttachments:
        this.getUser() &&
        this.organizationInfoLoaded &&
        !this.isAccreditorPlan(),
      supportedInitiatives:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().appPermissions.supportedInitiatives,
      authorizationSettings:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().appPermissions.institutionSettings,
      canCreateAssessmentPlans:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().appPermissions.canCreateAssessmentPlans,
      hasInstitutionDirectoryProfile:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().editDirectoryInstitutions && this.getUser().editDirectoryInstitutions.length > 0,
      hasProgramDirectoryProfile:
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().editProgramDirectoryInstitutions && this.getUser().editProgramDirectoryInstitutions.length > 0,
      otherRoles: //not used anymore, because of reviewers without programs
        this.getUser() &&
        this.organizationInfoLoaded &&
        this.getUser().userRoles.isEvaluator &&
        this.getEvaluatorRoles().length > 0,
      allowFacultyDashboard:
        this.getUser() &&
        this.getUser().appPermissions.allowFacultyDashboard,
      learningAssessment:
        this.getUser() &&
        this.getUser().userOrganizationData.allowLearningAssessment,
      hasMenuAssessment:
        this.getUser() &&
        this.getUser().appPermissions.hasMenuProjects,
      hasMenuLearningAssessment:
        this.getUser() &&
        this.getUser().appPermissions.hasMenuLearningAssessment,
      hasMenuProgramDirectory:
        this.getUser() &&
        this.getUser().appPermissions.canAccessProgramDirectory,
      hasMenuInstitutionalDirectory:
        this.getUser() &&
        this.getUser().appPermissions.canAccessDirectory,
      settings: this.getUser() &&
        this.getUser().weaveToolPermissions &&
        this.getUser().weaveToolPermissions.inviteUser,
      canCreateEditUsersUserGroups: false
    }
    /* hide settings for all
    this.getPermissionToShow.settings =
        this.getUser() &&
        this.organizationInfoLoaded &&
        (
          // this.getPermissionToShow.createEditUserGroup || //For now, we will not show the settings tab for the user groups
          // this.getPermissionToShow.institutionLogo ||
          this.getPermissionToShow.admin ||
          this.getPermissionToShow.manageStandards ||
          this.getPermissionToShow.manageProgramTemplates ||
          this.getPermissionToShow.createNewProgram ||
          this.getPermissionToShow.createProgramEvent ||
          this.getPermissionToShow.inviteUser
        );*/
    this.getPermissionToShow.canCreateEditUsersUserGroups =
        this.getPermissionToShow.editUsers ||
        this.getPermissionToShow.createEditUserGroup;
  }

  isEvaluatorWorkingForOtherInstitution(){
    const currentUser = this.getUser();
    if(currentUser && currentUser.userRoles.isEvaluator){
      return currentUser.evaluatorRoleProgramRequestKey;
    }
    return  false;
  }

  isEvaluatorWithNoPrograms(): boolean {
    const currentUser = this.getUser();
    return (
      currentUser &&
      currentUser.userData.currentUserAccount.accountName === "Independent Evaluators" &&
      !this.isEvaluatorWorkingForOtherInstitution()
    );
  }

  getEvaluatorProgram(): evaluatorRoleProgram{
    const currentUser = this.getUser();
    if(this.isEvaluatorWorkingForOtherInstitution()){
      return currentUser.evaluatorPrograms.find(program => program.requestKey === currentUser.evaluatorRoleProgramRequestKey);
    }
    return null
  }

  isFaculty(){
    const currentUser = this.getUser();
    return typeof currentUser !== undefined && currentUser !== null && currentUser.userRoles.isFaculty;
  }

  isProgramLeader() {
    const currentUser = this.getUser();
    return typeof currentUser !== undefined && currentUser._isProgramLeader;
  }

  isOrgAdmin() {
    const currentUser = this.getUser();
    return typeof currentUser !== undefined && currentUser !== null &&
      typeof currentUser.userRoles !== undefined && currentUser.userRoles !== null &&
      currentUser.userRoles.isOrgAdmin;
  }

  isNewApplicant(){
    const currentUser = this.getUser();
    return currentUser?.userData?.openApplicationRequestKey !== null && currentUser?.userData?.openApplicationRequestKey !== undefined && currentUser?.userData?.openApplicationRequestKey !== '';
  }

  getMaxPoints() {
    return this.getUser().userOrganizationData.learningAssessmentOutcomeMaxPoints;
  }

  getLightMode(){
    return this.lightModeSource.getValue();
  }
  getLightModeFromStorage() {
    return this.LocalStorage.getValue(this.localStorageKey, this.localStorageKey);
  }
  setLightModeToStorage() {
    this.LocalStorage.saveValue( this.localStorageKey, this.localStorageKey, this.lightModeSource.getValue());
  }
  setLightMode(lightMode: string){
    this.lightModeSource.next(lightMode);
    this.setLightModeToStorage();
  }

  getWidgetsList() {
    return JSON.parse(localStorage.getItem("widgets"));
  }
  setWidgetsList(widgetsList:SingleDashboardWidget[]) {
    let saveWidgetData = [];
    widgetsList.forEach(item => {
      let dashboardWidget = {
        userWidgetRequestKey: item.userRequestKey,
        widgetRequestKey: item.widgetRequestKey,
        widgetName: item.name,
        userWidgetParameters: item.options
      }
      saveWidgetData.push(dashboardWidget);
    });
    this.currentUserWidgetsList = saveWidgetData;
    localStorage.setItem('widgets', JSON.stringify(this.currentUserWidgetsList));
  }

  getTermsAccepted(): boolean{
    const currentUser = this.getUser();

    return !(currentUser &&
      (
        (currentUser.userData.isContractAdmin &&
          (
            !currentUser.userData.termsAndConditionsAdminAcceptedDate ||
            currentUser.userData.termsAndConditionsAdminAcceptedDate == AppConstants.invalidDate
          )
        ) ||
        (
          !currentUser.userData.isContractAdmin &&
          !currentUser.termsAndConditionsAcceptedDate
        )
      ));
  }

  programAdvisoryTabEnabled(programDirectoryRequestKey: string): boolean{
    const currentUser = this.getUser();
    if(currentUser){
      const programDirectory = currentUser.editProgramDirectoryInstitutions.find(inst => inst.directoryProgramRequestKey === programDirectoryRequestKey);
      if(programDirectory){
        return programDirectory.showAdvisoryTab;
      }
      else if(typeof currentUser.editProgramDirectoryShowAdvisoryTab !== 'undefined'){
        return currentUser.editProgramDirectoryShowAdvisoryTab;
      }
    }

    return false;
  }

  programAdvisoryTabEdit(programDirectoryRequestKey: string): boolean{
    const currentUser = this.getUser();
    if(currentUser){
      const programDirectory = currentUser.editProgramDirectoryInstitutions.find(inst => inst.directoryProgramRequestKey === programDirectoryRequestKey);
      if(programDirectory){
        return programDirectory.editAdvisoryTab;
      }
      else if(typeof currentUser.editProgramDirectoryEditAdvisoryTab !== 'undefined'){
        return currentUser.editProgramDirectoryEditAdvisoryTab;
      }
    }

    return false;
  }

  institutionalAdvisoryTabEnabled(institutionDirectoryRequestKey: string): boolean {
    const currentUser = this.getUser();
    if(currentUser && currentUser.editDirectoryInstitutions) {
      const institutionDirectory = currentUser.editDirectoryInstitutions.find(
        inst => inst.directoryInstitutionRequestKey === institutionDirectoryRequestKey
      );
      if(institutionDirectory && typeof institutionDirectory.showAdvisoryTab !== 'undefined') {
        return institutionDirectory.showAdvisoryTab;
      } else if (typeof currentUser.editInstitutionalDirectoryShowAdvisoryTab !== 'undefined') {
        return currentUser.editInstitutionalDirectoryShowAdvisoryTab;
      }
    }
    return false;
  }

  institutionalAdvisoryTabEdit(institutionDirectoryRequestKey: string): boolean {
    const currentUser = this.getUser();
    if(currentUser && currentUser.editDirectoryInstitutions) {
      const institutionDirectory = currentUser.editDirectoryInstitutions.find(
        inst => inst.directoryInstitutionRequestKey === institutionDirectoryRequestKey
      );
      if(institutionDirectory && typeof institutionDirectory.editAdvisoryTab !== 'undefined') {
        return institutionDirectory.editAdvisoryTab;
      } else if (typeof currentUser.editInstitutionalDirectoryEditAdvisoryTab !== 'undefined') {
        return currentUser.editInstitutionalDirectoryEditAdvisoryTab;
      }
    }
    return false;
  }

  programFacultyTabEnabled(programDirectoryRequestKey: string): boolean{
    const currentUser = this.getUser();
    if(currentUser){
      const programDirectory = currentUser.editProgramDirectoryInstitutions.find(inst => inst.directoryProgramRequestKey === programDirectoryRequestKey);
      if(programDirectory){
        return programDirectory.showFacultyTab;
      }
      else if(typeof currentUser.editProgramDirectoryShowFacultyTab !== 'undefined'){
        return currentUser.editProgramDirectoryShowFacultyTab;
      }
    }
    return false;
  }

  programFacultyTabEdit(programDirectoryRequestKey: string): boolean{
    const currentUser = this.getUser();
    if(currentUser){
      const programDirectory = currentUser.editProgramDirectoryInstitutions.find(inst => inst.directoryProgramRequestKey === programDirectoryRequestKey);
      if(programDirectory){
        return programDirectory.editFacultyTab;
      }
      else if(typeof currentUser.editProgramDirectoryEditFacultyTab !== 'undefined'){
        return currentUser.editProgramDirectoryEditFacultyTab;
      }
    }
    return false;
  }

  institutionalFacultyTabEnabled(institutionDirectoryRequestKey: string): boolean {
    const currentUser = this.getUser();
    if(currentUser && currentUser.editDirectoryInstitutions) {
      const institutionDirectory = currentUser.editDirectoryInstitutions.find(
        inst => inst.directoryInstitutionRequestKey === institutionDirectoryRequestKey
      );
      if(institutionDirectory && typeof institutionDirectory.showFacultyTab !== 'undefined') {
        return institutionDirectory.showFacultyTab;
      } else if (typeof currentUser.editInstitutionalDirectoryShowFacultyTab !== 'undefined') {
        return currentUser.editInstitutionalDirectoryShowFacultyTab;
      }
    }
    return false;
  }

  institutionalFacultyTabEdit(institutionDirectoryRequestKey: string): boolean {
    const currentUser = this.getUser();
    if(currentUser && currentUser.editDirectoryInstitutions) {
      const institutionDirectory = currentUser.editDirectoryInstitutions.find(
        inst => inst.directoryInstitutionRequestKey === institutionDirectoryRequestKey
      );
      if(institutionDirectory && typeof institutionDirectory.editFacultyTab !== 'undefined') {
        return institutionDirectory.editFacultyTab;
      } else if (typeof currentUser.editInstitutionalDirectoryEditFacultyTab !== 'undefined') {
        return currentUser.editInstitutionalDirectoryEditFacultyTab;
      }
    }
    return false;
  }

}

export interface evaluatorRole{
  program: evaluatorRoleProgram,
  institutionName: string,
  accreditor: {
    abbreviation: string,
    name: string,
    thumbnail: string,
    logo: string,
  },
  role: string,
  programName: string
}

export interface evaluatorRoleProgram{
  requestKey: string,
  name: string,
  institutionDirectoryRequestKey: string,
  programDirectoryProgramRequestKey: string,
  programDirectoryRequestKey: string,
}

export interface accreditationProfile{
  accreditorLogo: string,
  accreditorName: string,
  directoryInstitutionRequestKey: string,
}

export interface programProfile {
  accreditorLogo: string,
  accreditorName: string,
  accreditorRequestKey: string,
  directoryProgramRequestKey: string,
  institutionRequestKey: string,
  showAdvisoryTab: boolean,
  editAdvisoryTab: boolean,
  annualUpdateCaption: string
}

export enum DashboardType{
  accreditor = 1,
  school,
  programLeader,
  faculty,
  newApplicant
}

export interface menuPermissions {
  dashboard: boolean,
  projects: boolean,
  credentials: boolean,
  institutions: boolean,
  institutionsDirectory: boolean,
  programsDirectory: boolean,
  federationDirectory: boolean,
  isAccreditorDirectoryInstitution: boolean,
  isAccreditorDirectoryLiaison: boolean,
  showApplicationTab: boolean,
  standards: boolean, //deprecated
  evaluators: boolean,
  canReturnFeedback: boolean,
  reports: boolean,
  canImpersonate: boolean,
  canSearch: boolean,
  canCreateNewProject: boolean,
  attachments: boolean,
  organizationSettings: boolean,
  courses: boolean,
  importTool: boolean,
  templates: boolean,
  ilo: boolean,
  editUserProfile: boolean,
  editUsers: boolean,
  editUsersTab: boolean,
  createEditUserGroup: boolean,
  reportSettings: boolean,
  inviteUser: boolean,
  createNewProgram: boolean,
  createProgramEvent: boolean,
  manageProgramTemplates: boolean,
  manageStandardsAsAccreditor: boolean,
  manageProgramTemplatesAsAccreditor: boolean,
  institutionLogo: boolean,
  manageStandards: boolean,
  errorLog: boolean,
  uiKit: boolean,
  eventLog: boolean,
  admin: boolean,
  annualReportsTemplateEdit: boolean,
  projectAttachments: boolean,
  supportedInitiatives: boolean,
  authorizationSettings: boolean,
  canCreateAssessmentPlans: boolean,
  hasInstitutionDirectoryProfile: boolean,
  hasProgramDirectoryProfile: boolean,
  otherRoles: boolean,
  allowFacultyDashboard: boolean,
  learningAssessment: boolean,
  hasMenuAssessment: boolean,
  hasMenuLearningAssessment: boolean,
  settings: boolean,
  canCreateEditUsersUserGroups: boolean,
  hasMenuProgramDirectory: boolean,
  hasMenuInstitutionalDirectory: boolean
}

//interface for user roles
export interface userRoles {
  sysAdmin: boolean,
  pcAdmin: boolean,
  isOrgAdmin: boolean,
  isEvaluator: boolean,
  isFaculty: boolean,
  isVerifyManager: boolean,
  isVerifyFacilitator: boolean,
  isContractAdmin: boolean
}

//interface for organization info
export interface organizationInfo {
  name: string,
  description: string,
  address1: string,
  address2: string,
  city: string,
  state: string,
  zip: string,
  country: string,
  phone: string,
  fax: string,
  website: string,
  logo: string,
  allowCredentials: boolean,
  allowAssessment: boolean,
  isAccreditor: boolean,
  isFederation: boolean,
  authenticationMethodGuid: string,
  isFreemium: boolean,
  learningAssessmentOutcomeMaxPoints?: number,
  organizationSizeId: number,
  organizationTypeId: number,
  organizationRequestKey: string,
  allowSingleSignOn: boolean,
  allowLearningAssessment: boolean,
  externalId: string,
  parentOrganizationRequestKey: string,
  parentOrganizationName: string,
  organizationType: string,
  organizationSize: string,
  assessmentEnabled: boolean,
  credentialsEnabled: boolean,
  systemId: number,
  systemName: string
}

export interface accountInfo {
  accountName: string,
  accountRequestKey: string,
  accredProjectName: string,
  accredProjectRequestKey: string,
  accreditorName: string,
  accreditorAbbr: string,
  annualUpdateRequestKey: string,
  directoryInstitutionRequestKey: string,
  directoryProgramRequestKey: string,
  userRole: UserRole,
  nameToShow?: string,
  isAccreditor?: boolean,
  isFederation?: boolean,
}
export enum UserRole {
  teamMember = 1,
  admin = 2,
  evaluator = 3
}
